import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { ChevronLeft, CircleCheck, Loader2 } from 'lucide-react'

import { useLocation, useNavigate } from 'react-router-dom'

import { useUserContext } from '@/hooks/user'

import { AppContext } from '@/components/base/appcontext'
import BaseRadio from '@/components/base/radiogroup'
import Loading from '@/components/base/loading'
import UndefinedUser from '@/components/base/undefinedUser'
import NotFound from '@/components/base/notFound'
import { OnboardingConfig } from '../../domain/onboarding'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Typography } from '@/components/ui/typography'
import {
    SUPPORTED_LANGUAGES,
    getStoredUserLanguage,
    resolveInitialLanguage,
    setUserLanguage
} from '@/lib/languages'
import { cn } from '@/lib/utils'
import { updateProfile } from '@/lib/profileUpdate'
import Markdown from '../base/markdown'

function getTelegramLoginFallback(user?: {
    username?: string
    first_name?: string
}): string {
    return user?.username || user?.first_name || ''
}

/** Language codes the app offers, from the `onboarding.language` options. */
function supportedLanguages(options?: { options?: Record<string, unknown> }) {
    const codes = Object.values(options?.options || {}).filter(
        (value): value is string => typeof value === 'string'
    )
    return codes.length > 0 ? codes : SUPPORTED_LANGUAGES
}

// The language question is not asked on first run: it is taken from the
// Telegram client (or English) and can be changed later in the profile.
const LANGUAGE_STEP = 'language'

const CONSENT_TOC_KEY = 'onboarding.consent.toc'
const CONSENT_TOC_TGID_KEY = 'onboarding.consent.toc-tgid'
const CONSENT_PP_KEY = 'onboarding.consent.pp'
const CONSENT_PP_TGID_KEY = 'onboarding.consent.pp-tgid'

// Set only after the profile is successfully submitted to the backend.
// The consent keys above are written as soon as the user signs off (legal
// record), so they must NOT be used as the "onboarding done" gate — a user
// who clicks consent and closes the app would otherwise skip the rest of
// onboarding on reload and be left with no profile on the backend.
/** Matches the durations declared for the onboarding classes in index.css. */
const STEP_EXIT_MS = 160
const STEP_ENTER_MS = 220

type StepDirection = 'forward' | 'back'
type StepMotion = {
    phase: 'idle' | 'leaving' | 'entering'
    direction: StepDirection
}

function stepMotionClass({ phase, direction }: StepMotion): string {
    if (phase === 'idle') return ''
    const suffix = direction === 'forward' ? 'forward' : 'back'
    return phase === 'leaving'
        ? `onboarding-leave-${suffix}`
        : `onboarding-enter-${suffix}`
}

export const ONBOARDING_COMPLETED_KEY = 'onboarding.completed'

// Stable, language-independent draft key. The previous key came from
// geti18n(), which returns '...' before i18n loads and a translated
// string after — so saved progress was written under one key and read
// under another, and interrupted sessions always restarted from scratch.
const ONBOARDING_FORM_STORAGE_KEY = 'onboarding.formdata'

function OnboardingForm({
    onboardingData
}: {
    onboardingData: OnboardingConfig[]
}): React.ReactElement {
    const appContext = useContext(AppContext)
    const userContext = useUserContext()
    const navigate = useNavigate()
    const location = useLocation()

    const steps = useMemo(
        () => onboardingData.filter((s) => s.name !== LANGUAGE_STEP),
        [onboardingData]
    )
    const languages = supportedLanguages(
        appContext.getOptions('onboarding.language')
    )
    const initialLanguage = (languageCode?: string) =>
        getStoredUserLanguage() ||
        resolveInitialLanguage(languageCode, languages)

    const [step, setStep] = useState(0)
    const [submit, setSubmit] = useState(false)
    const [formData, setFormData] = useState<{ [key: string]: any }>(() => {
        let savedFormData: { [key: string]: any } = {}
        try {
            savedFormData = JSON.parse(
                localStorage.getItem(ONBOARDING_FORM_STORAGE_KEY) || '{}'
            )
        } catch (error) {
            console.error('Failed to restore onboarding draft:', error)
        }

        if (!savedFormData.language && userContext.user) {
            savedFormData.language = initialLanguage(
                userContext.user.language_code
            )
        }
        // geti18n reads the language when called, so it must be applied
        // before the first render: nothing re-renders the screen otherwise.
        if (savedFormData.language) setUserLanguage(savedFormData.language)

        if (savedFormData.login) {
            return savedFormData
        }

        return {
            ...savedFormData,
            login: getTelegramLoginFallback(userContext.user)
        }
    })
    const [stepMotion, setStepMotion] = useState<StepMotion>({
        phase: 'idle',
        direction: 'forward'
    })
    const [showFinalScreen, setShowFinalScreen] = useState(false)
    const [error, setError] = useState('')
    // The final screen has nothing left to answer, so it submits itself. The
    // ref makes that happen once: StrictMode runs effects twice in dev, and a
    // second submit would POST the profile again.
    const autoConfirmed = useRef(false)

    const currentStep = steps[step]

    // Persist the draft as the user goes, so an interrupted session
    // resumes with its answers instead of restarting from scratch.
    useEffect(() => {
        try {
            localStorage.setItem(
                ONBOARDING_FORM_STORAGE_KEY,
                JSON.stringify(formData)
            )
        } catch (error) {
            console.error('Failed to save onboarding draft:', error)
        }
    }, [formData])

    // The Telegram user arrived after the first render: pick the language
    // now. The state update re-renders the screen in it.
    useEffect(() => {
        if (!userContext.user || formData.language) return
        const language = initialLanguage(userContext.user.language_code)
        setUserLanguage(language)
        handleInputChange('language', language)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userContext.user])

    useEffect(() => {
        const defaultLogin = getTelegramLoginFallback(userContext.user)
        if (!defaultLogin) return

        setFormData((currentFormData) => {
            if (currentFormData.login) return currentFormData

            return {
                ...currentFormData,
                login: defaultLogin
            }
        })
    }, [userContext.user])

    // The step only swaps once the outgoing one has left, then the incoming
    // one animates in; running both at once made the panel appear to jump.
    const moveToStep = (
        direction: StepDirection,
        nextStep: (prev: number) => number
    ) => {
        setStepMotion({ phase: 'leaving', direction })
        window.setTimeout(() => {
            setStep(nextStep)
            setStepMotion({ phase: 'entering', direction })
            window.setTimeout(
                () => setStepMotion({ phase: 'idle', direction }),
                STEP_ENTER_MS
            )
        }, STEP_EXIT_MS)
    }

    const handleNext = () => {
        if (step < steps.length - 1) {
            moveToStep('forward', (prev) =>
                Math.min(prev + 1, steps.length - 1)
            )
        } else {
            setShowFinalScreen(true)
        }
    }

    const handleBack = () => {
        if (step > 0) {
            moveToStep('back', (prev) => Math.max(prev - 1, 0))
        }
    }

    const handleInputChange = (key: string, value: any) => {
        setFormData((prev) => ({
            ...prev,
            [key]: value
        }))
    }

    const signConsent = () => {
        const signOffTime = new Date().toUTCString()
        const consentValue = `${userContext.user?.id.toString()}-${signOffTime}`

        // Best effort: Safari private mode can throw on localStorage writes.
        // The sign-off also lives in formData, so the flow must not break here.
        try {
            localStorage.setItem(currentStep.name, signOffTime)
            localStorage.setItem(
                currentStep.name + '-tgid',
                userContext.user?.id.toString() || ''
            )

            if (currentStep.name === 'onboarding.consent.combined') {
                localStorage.setItem(CONSENT_TOC_KEY, signOffTime)
                localStorage.setItem(
                    CONSENT_TOC_TGID_KEY,
                    userContext.user?.id.toString() || ''
                )
                localStorage.setItem(CONSENT_PP_KEY, signOffTime)
                localStorage.setItem(
                    CONSENT_PP_TGID_KEY,
                    userContext.user?.id.toString() || ''
                )
            }
        } catch (error) {
            console.error('Failed to persist consent sign-off:', error)
        }

        if (currentStep.name === 'onboarding.consent.combined') {
            handleInputChange('onboarding.consent.toc', consentValue)
            handleInputChange('onboarding.consent.pp', consentValue)
        }

        handleInputChange(currentStep.name, consentValue)
        handleNext()
    }

    const placeholder =
        currentStep?.placeholder && appContext.geti18n(currentStep.placeholder)

    // Enter on the keyboard moves on, like the Next button.
    const nextOnEnter = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && formData[currentStep.name]) {
            e.preventDefault()
            handleNext()
        }
    }

    const renderInput = () => {
        switch (currentStep.kind) {
            case 'consent':
                return formData[currentStep.name] ? (
                    <div className="mt-6 flex items-start gap-2 rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm">
                        <CircleCheck
                            aria-hidden
                            className="mt-0.5 size-[18px] shrink-0 text-primary"
                        />
                        <Markdown
                            text={formData[currentStep.name].split('-').pop()}
                        />
                    </div>
                ) : null
            case 'input':
            case 'number':
                return (
                    <Input
                        type={currentStep.kind === 'number' ? 'number' : 'text'}
                        className="mt-6 h-12 rounded-xl text-lg"
                        aria-labelledby="onboarding-title"
                        placeholder={placeholder}
                        value={formData[currentStep.name] ?? ''}
                        onChange={(e) =>
                            handleInputChange(currentStep.name, e.target.value)
                        }
                        onKeyDown={nextOnEnter}
                        enterKeyHint="next"
                        autoFocus
                        name={currentStep.name}
                        required
                    />
                )
            case 'textarea':
                return (
                    <Textarea
                        className="mt-6 rounded-xl text-base"
                        aria-labelledby="onboarding-title"
                        placeholder={placeholder}
                        rows={5}
                        name={currentStep.name}
                        value={formData[currentStep.name] ?? ''}
                        onChange={(e) =>
                            handleInputChange(currentStep.name, e.target.value)
                        }
                        autoFocus
                        required
                    />
                )
            case 'singleselect':
            case 'multiselect':
                return (
                    <BaseRadio
                        variant="list"
                        labelledBy={
                            currentStep.title ? 'onboarding-title' : undefined
                        }
                        className="mt-6"
                        data={appContext.getOptions(currentStep.options || '')}
                        name={currentStep.name}
                        value={formData[currentStep.name]}
                        onChange={async (e, name, value) => {
                            handleInputChange(currentStep.name, value)
                            // Tapping the selected row clears it; only a
                            // pick moves on.
                            if (value) handleNext()
                        }}
                        skipTranslate={currentStep.name === 'age'}
                        required
                    />
                )
            default:
                return (
                    <Typography className="mt-6 text-destructive">
                        unknown field type
                    </Typography>
                )
        }
    }

    const renderActions = () => {
        const answered = Boolean(formData[currentStep.name])
        const isSelect =
            currentStep.kind === 'singleselect' ||
            currentStep.kind === 'multiselect'
        const nextButton = (
            <Button
                size="lg"
                className="h-12 w-full rounded-xl text-base"
                onClick={handleNext}
                disabled={!answered}
            >
                {appContext.geti18n('onboarding.next.button.text')}
            </Button>
        )

        let primary: React.ReactNode = null
        if (currentStep.kind === 'consent') {
            primary = answered ? (
                nextButton
            ) : (
                <Button
                    size="lg"
                    className="h-12 w-full rounded-xl text-base"
                    onClick={signConsent}
                >
                    {appContext.geti18n('onboarding.toc.button.text')}
                </Button>
            )
        } else if (!isSelect || answered) {
            // A select moves on by itself; Next only returns for an answer
            // the user already gave and came back to.
            primary = nextButton
        }

        const canSkip = currentStep.kind !== 'consent' && !currentStep.required

        return (
            <>
                {primary}
                {canSkip && (
                    <Button
                        size="lg"
                        variant="ghost"
                        className="h-11 w-full rounded-xl text-muted-foreground"
                        onClick={handleNext}
                    >
                        {appContext.geti18n(currentStep.skipText || 'Skip')}
                    </Button>
                )}
            </>
        )
    }

    const handleConfirm = async () => {
        setError('')
        setSubmit(true)
        const language =
            formData['language'] ||
            initialLanguage(userContext.user?.language_code)
        setUserLanguage(language)
        try {
            const tocConsent =
                formData['onboarding.consent.toc'] ||
                formData['onboarding.consent.combined']
            const ppConsent =
                formData['onboarding.consent.pp'] ||
                formData['onboarding.consent.combined']

            // Consent value is `${telegramId}-${signOffTime}`; the id may be
            // negative, so split('-') is wrong — take everything after the
            // id instead. Interrupted sessions lose in-memory formData but
            // keep the sign-off in its own localStorage key (written on the
            // consent click) — fall back to it instead of crashing.
            const consentSignOffTime = (value?: string) =>
                value?.match(/^-?\d+-(.*)$/)?.[1]

            const tocTime = new Date(
                consentSignOffTime(tocConsent) ||
                    localStorage.getItem(CONSENT_TOC_KEY) ||
                    ''
            ).getTime()
            const ppTime = new Date(
                consentSignOffTime(ppConsent) ||
                    localStorage.getItem(CONSENT_PP_KEY) ||
                    ''
            ).getTime()

            if (!Number.isFinite(tocTime) || !Number.isFinite(ppTime)) {
                setError(appContext.geti18n('onboarding.error.consentMissing'))
                return
            }

            // Backend declares the consent fields as int32 — a fractional
            // value fails the whole JSON decode with a 400.
            // Mobile networks inside the Telegram webview hang requests
            // without failing them; without an abort the button stays
            // disabled forever and the screen looks frozen.
            const abortController = new AbortController()
            const abortTimeout = setTimeout(
                () => abortController.abort(),
                20000
            )
            try {
                // updateProfile adds the device timezone (marketing
                // messages respect the user's night).
                await updateProfile(
                    {
                        tocConcent: Math.floor(tocTime / 1000),
                        ppConcent: Math.floor(ppTime / 1000),
                        login: formData['login'],
                        bio: '',
                        goal: formData['goal'],
                        age: formData['age'],
                        language
                    },
                    { signal: abortController.signal }
                )
            } finally {
                clearTimeout(abortTimeout)
            }
            try {
                localStorage.setItem(
                    ONBOARDING_FORM_STORAGE_KEY,
                    JSON.stringify(formData)
                )
                localStorage.setItem(
                    ONBOARDING_COMPLETED_KEY,
                    new Date().toUTCString()
                )
            } catch (storageError) {
                // Profile is already saved on the backend; a storage failure
                // here must not surface as a submit error.
                console.error(
                    'Failed to persist onboarding completion:',
                    storageError
                )
            }
            if (location.pathname !== '/onboarding') {
                navigate(0)
            } else {
                navigate('/?successLogin=' + new Date().getTime())
            }
        } catch (error) {
            // Do NOT reload here: consent is already in localStorage, so a
            // reload passes the onboarding gate and silently drops the user
            // on the home screen with an incomplete profile. Show the error
            // and let them retry.
            let errorMessage: string
            if (error instanceof Error && error.name === 'AbortError') {
                errorMessage = appContext.geti18n(
                    'programmes.report.result.error'
                )
            } else if (error instanceof Error) {
                errorMessage = error.message
            } else if (typeof error === 'string') {
                errorMessage = error
            } else {
                console.error(error)
                errorMessage = 'Unknown error'
            }
            setError(errorMessage)
        } finally {
            if (location.state?.profile) {
                location.state = {
                    ...location.state,
                    profile: undefined
                }
            }
            setSubmit(false)
        }
    }

    useEffect(() => {
        if (!showFinalScreen || autoConfirmed.current) return
        autoConfirmed.current = true
        void handleConfirm()
        // handleConfirm is recreated every render; the ref, not the dep list,
        // is what keeps this to a single run.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showFinalScreen])

    if (!userContext.user) return <UndefinedUser />
    if (!currentStep) return <NotFound />

    const filledSegments = showFinalScreen ? steps.length : step + 1

    return (
        <div className="mx-auto flex min-h-[100dvh] w-full max-w-[560px] flex-col px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]">
            <div className="flex h-12 shrink-0 items-center gap-3">
                <Button
                    size="icon"
                    variant="ghost"
                    className={cn(
                        '-ml-2 rounded-full',
                        (step === 0 || showFinalScreen) && 'invisible'
                    )}
                    onClick={handleBack}
                    aria-label={appContext.geti18n(
                        'base.error.button.back.text'
                    )}
                >
                    <ChevronLeft className="size-5" />
                </Button>
                <div
                    className="flex flex-1 gap-1.5"
                    role="progressbar"
                    aria-valuemin={1}
                    aria-valuemax={steps.length}
                    aria-valuenow={filledSegments}
                >
                    {steps.map((s, i) => (
                        <span
                            key={s.name}
                            className={cn(
                                'h-1 flex-1 rounded-full transition-colors duration-300 motion-reduce:transition-none',
                                i < filledSegments ? 'bg-primary' : 'bg-border'
                            )}
                        />
                    ))}
                </div>
                {/* Mirrors the back button so the bar stays centered. */}
                <span className="size-9 shrink-0 -mr-2" aria-hidden />
            </div>

            <div
                className={`${stepMotionClass(stepMotion)} onboarding-step flex flex-1 flex-col pt-8`}
            >
                {!showFinalScreen ? (
                    <>
                        {currentStep.title && (
                            <h1
                                id="onboarding-title"
                                className="text-2xl font-semibold leading-tight tracking-tight text-foreground"
                            >
                                {appContext.geti18n(currentStep.title)}
                            </h1>
                        )}
                        {currentStep.text && (
                            <div className="mt-3 max-w-[65ch] text-base leading-relaxed text-muted-foreground">
                                {currentStep.kind === 'consent' ? (
                                    <Markdown
                                        text={appContext.geti18n(
                                            currentStep.text
                                        )}
                                    />
                                ) : (
                                    appContext.geti18n(currentStep.text)
                                )}
                            </div>
                        )}
                        {renderInput()}
                    </>
                ) : (
                    <h1 className="text-2xl font-semibold leading-tight tracking-tight text-foreground">
                        {appContext.geti18n('onboarding.final.title')}
                    </h1>
                )}
            </div>

            <div className="sticky bottom-0 flex shrink-0 flex-col gap-2 bg-background pt-4">
                {error && (
                    <Alert variant="destructive" className="rounded-xl">
                        <AlertDescription>
                            <Typography variant="body-sm">{error}</Typography>
                        </AlertDescription>
                    </Alert>
                )}
                {!showFinalScreen ? (
                    renderActions()
                ) : (
                    // This screen submits itself, so the button is only the
                    // retry after a failure; otherwise it shows progress.
                    <Button
                        size="lg"
                        className="h-12 w-full rounded-xl text-base"
                        onClick={handleConfirm}
                        disabled={submit}
                        aria-busy={submit}
                    >
                        {submit && (
                            <Loader2
                                aria-hidden
                                className="size-5 animate-spin"
                            />
                        )}
                        {appContext.geti18n(
                            error
                                ? 'base.static.retry.text'
                                : 'onboarding.confirm.button.text'
                        )}
                    </Button>
                )}
            </div>
        </div>
    )
}

export default OnboardingForm
